import HelpOutlineRoundedIcon from "@mui/icons-material/HelpOutlineRounded";
import { Box, InputBase, Stack, Typography } from "@mui/material";
import type { KeyboardEvent, RefObject } from "react";
import { useCallback, useState } from "react";
import { useIntercomContext } from "@entrilia/shared/contexts/IntercomContext";
import useDebounce from "@entrilia/shared/hooks/useDebounce";
import { intercomArticleIds } from "../../../../../../../../../utilities/intercomArticles";
import CustomFormulaHighlightedText from "./CustomFormulaHighlightedText";
import CustomFormulaSuggestionsPopup from "./CustomFormulaSuggestionsPopup";
import {
  clampSuggestionIndex,
  getNextSuggestionIndex,
  getPreviousSuggestionIndex,
} from "./utilities/customFormulaAutocomplete";
import type { FormulaReferenceField } from "./utilities/customFormulaParser";
import type { FormulaSuggestion } from "./utilities/customFormulaSuggestions";
import {
  applyFormulaSuggestionAtCaret,
  getFormulaSuggestionContext,
  getFormulaSuggestions,
} from "./utilities/customFormulaSuggestions";

interface Props {
  formulaInput: string;
  formulaError?: string;
  referenceFields: FormulaReferenceField[];
  functions?: string[];
  inputRef: RefObject<HTMLInputElement | HTMLTextAreaElement | null>;
  onFormulaChange: (value: string) => void;
  onFormulaBlur: () => void;
}

const CustomFormulaEditorPanel = ({
  formulaInput,
  formulaError,
  referenceFields,
  functions,
  inputRef,
  onFormulaChange,
  onFormulaBlur,
}: Props) => {
  const [suggestions, setSuggestions] = useState<FormulaSuggestion[]>([]);
  const [suggestionQuery, setSuggestionQuery] = useState("");
  const [selectedSuggestionIndex, setSelectedSuggestionIndex] = useState(0);
  const [isSuggestionsOpen, setIsSuggestionsOpen] = useState(false);
  const [inputScrollTop, setInputScrollTop] = useState(0);
  const { showArticle } = useIntercomContext();

  const updateSuggestions = (value: string, caretIndex: number): void => {
    const suggestionContext = getFormulaSuggestionContext(value, caretIndex);
    if (suggestionContext.query.length === 0) {
      setIsSuggestionsOpen(false);
      setSuggestions([]);
      setSuggestionQuery("");
      setSelectedSuggestionIndex(0);
      return;
    }

    const nextSuggestions = getFormulaSuggestions(value, caretIndex, referenceFields, functions);
    if (nextSuggestions.length === 0) {
      setIsSuggestionsOpen(false);
      setSuggestions([]);
      setSuggestionQuery("");
      setSelectedSuggestionIndex(0);
      return;
    }

    setSuggestions(nextSuggestions);
    setSuggestionQuery(suggestionContext.query);
    setSelectedSuggestionIndex((prevIndex) => clampSuggestionIndex(prevIndex, nextSuggestions.length));
    setIsSuggestionsOpen(true);
  };

  const debouncedUpdateSuggestions = useDebounce(updateSuggestions, 100);

  const getCurrentCaretIndex = (): number => {
    const selectionStart = inputRef.current?.selectionStart;
    if (selectionStart === undefined || selectionStart === null) {
      return formulaInput.length;
    }

    return selectionStart;
  };

  const focusInputAt = (index: number): void => {
    requestAnimationFrame(() => {
      if (!inputRef.current) {
        return;
      }
      inputRef.current.focus();
      inputRef.current.setSelectionRange(index, index);
    });
  };

  const applySuggestion = (suggestion: FormulaSuggestion): void => {
    const currentInput = inputRef.current?.value ?? formulaInput;
    const caretIndex = getCurrentCaretIndex();
    const applyResult = applyFormulaSuggestionAtCaret(currentInput, caretIndex, suggestion);
    onFormulaChange(applyResult.value);
    setIsSuggestionsOpen(false);
    setSuggestions([]);
    setSuggestionQuery("");
    setSelectedSuggestionIndex(0);
    focusInputAt(applyResult.caretIndex);
  };

  const handleEditorKeyDown = (event: KeyboardEvent<HTMLInputElement | HTMLTextAreaElement>): void => {
    if (!isSuggestionsOpen || suggestions.length === 0) {
      return;
    }

    if (event.key === "ArrowDown") {
      event.preventDefault();
      setSelectedSuggestionIndex((prevIndex) => getNextSuggestionIndex(prevIndex, suggestions.length));
      return;
    }

    if (event.key === "ArrowUp") {
      event.preventDefault();
      setSelectedSuggestionIndex((prevIndex) => getPreviousSuggestionIndex(prevIndex, suggestions.length));
      return;
    }

    if (event.key === "Enter" || event.key === "Tab") {
      const selectedSuggestion = suggestions[selectedSuggestionIndex];
      if (selectedSuggestion) {
        event.preventDefault();
        applySuggestion(selectedSuggestion);
      }
      return;
    }

    if (event.key === "Escape") {
      event.preventDefault();
      setIsSuggestionsOpen(false);
      setSuggestionQuery("");
      return;
    }
  };

  const handleInputCaretChange = (): void => {
    const currentInput = inputRef.current?.value ?? formulaInput;
    debouncedUpdateSuggestions(currentInput, getCurrentCaretIndex());
  };

  const handleShowArticle = useCallback(() => {
    showArticle(intercomArticleIds.FORMULA_EDITOR_GUIDE);
  }, [showArticle]);

  return (
    <Stack spacing={1.25} sx={{ p: 2.5 }}>
      <Box sx={{ display: "flex", alignItems: "center", gap: 0.75 }}>
        <Typography variant="subtitle1">Formula</Typography>
        <HelpOutlineRoundedIcon
          color="action"
          fontSize="small"
          sx={{ cursor: "pointer" }}
          onClick={handleShowArticle}
        />
      </Box>
      <Box
        sx={(theme) => ({
          border: `1px solid ${theme.palette.divider}`,
          borderRadius: "4px",
          bgcolor: theme.palette.background.grey,
          minHeight: 160,
          px: 2,
          py: 1.5,
        })}
      >
        <Box sx={{ display: "flex", alignItems: "flex-start", gap: 1 }}>
          <Typography variant="body2" color="textSecondary" sx={{ lineHeight: "24px", pt: "1px" }}>
            =
          </Typography>
          <Box sx={{ position: "relative", flex: 1 }}>
            <InputBase
              placeholder="Enter formula"
              value={formulaInput}
              inputRef={inputRef}
              onChange={(event) => {
                const value = event.target.value;
                onFormulaChange(value);
                debouncedUpdateSuggestions(value, event.target.selectionStart ?? value.length);
              }}
              inputProps={{
                maxLength: 400,
                spellCheck: false,
                autoComplete: "off",
                autoCorrect: "off",
                autoCapitalize: "off",
                onScroll: () => setInputScrollTop(inputRef.current?.scrollTop ?? 0),
              }}
              onKeyDown={handleEditorKeyDown}
              onClick={handleInputCaretChange}
              onKeyUp={handleInputCaretChange}
              onFocus={handleInputCaretChange}
              onBlur={() => {
                setIsSuggestionsOpen(false);
                onFormulaBlur();
              }}
              multiline
              minRows={1}
              sx={(theme) => ({
                flex: 1,
                width: "100%",
                lineHeight: "24px",
                "& .MuiInputBase-input": {
                  position: "relative",
                  zIndex: 1,
                  padding: 0,
                  maxHeight: "126px",
                  overflowY: "auto",
                  overflowX: "hidden",
                  whiteSpace: "pre-wrap",
                  wordBreak: "break-word",
                  resize: "none",
                  color: formulaInput.length > 0 ? "transparent" : theme.palette.text.primary,
                  caretColor: theme.palette.text.primary,
                },
                "& .MuiInputBase-input::placeholder": {
                  color: theme.palette.text.disabled,
                  opacity: 1,
                },
              })}
            />
            <CustomFormulaHighlightedText
              formulaInput={formulaInput}
              scrollTop={inputScrollTop}
              referenceFields={referenceFields}
            />
            <CustomFormulaSuggestionsPopup
              open={isSuggestionsOpen}
              suggestions={suggestions}
              query={suggestionQuery}
              selectedIndex={selectedSuggestionIndex}
              onSelectedIndexChange={setSelectedSuggestionIndex}
              onSuggestionSelect={applySuggestion}
            />
          </Box>
        </Box>
      </Box>
      {formulaError ? (
        <Typography variant="body2" sx={(theme) => ({ color: theme.palette.error.dark })}>
          {formulaError}
        </Typography>
      ) : (
        <Typography variant="body2" color="textSecondary">
          Example : &quot;Balance&quot; + &quot;Commitment&quot; / &quot;Amount&quot;
        </Typography>
      )}
    </Stack>
  );
};

export default CustomFormulaEditorPanel;
